import React, {useEffect, useMemo, useRef, useState} from "react";
import Spinner from "./helpers/spinner";
import {isPrivateRoute} from "./utils/private-paths";
import {userProfileService} from "./service/userProfile/userProfile.service";
import {useAuth0} from "@auth0/auth0-react";
import {useLocation} from "react-router-dom";


const isFlagEnabled = (value) => value === true || value === "true";

const syncUserWithBackend = async (user) => {
    try {
        if (!user) return null;

        const actions = [];

        const emailFlag = user["email_sync_pending"];
        const phoneFlag = user["phone_sync_pending"];

        if (isFlagEnabled(emailFlag)) {
            actions.push({
                promise: userProfileService.updateEmail(),
                successMsg: "Email updated successfully.",
                errorMsg: "Failed to update email.",
            });
        }

        if (isFlagEnabled(phoneFlag)) {
            actions.push({
                promise: userProfileService.updatePhone(),
                successMsg: "Phone updated successfully.",
                errorMsg: "Failed to update phone.",
            });
        }

        if (actions.length === 0) {
            return null;
        }

        const responses = await Promise.all(
            actions.map((action) => action.promise)
        );

        const messages = [];
        let hasError = false;

        responses.forEach((response, index) => {
            if (response?.statusCode === "200" || response?.statusCode === 200) {
                messages.push(actions[index].successMsg);
            } else if (response?.statusCode === "404" || response?.statusCode === 404) {
                messages.push(actions[index].successMsg);
            } else {
                hasError = true;
                messages.push(actions[index].errorMsg);
            }
        });

        return {
            type: hasError ? "danger" : "success",
            msg: messages.join(" "),
        };
    } catch (error) {
        console.error("Sync failed:", error);

        return {
            type: "danger",
            msg: "Failed to sync user details.",
        };
    }
};

function AuthorizedComponent({children}) {
    const auth = useAuth0();

    const [isSynced, setIsSynced] = useState(false);
    const [hasTriedSignin, setHasTriedSignin] = useState(false);
    const [message, setMessage] = useState(null);
    const hasSyncedRef = useRef(false);

    const location = useLocation();
    const locationPath = location.pathname;

    const isPrivate = useMemo(
        () => isPrivateRoute(locationPath),
        [locationPath]
    );

    const shouldRedirect = useMemo(() => {
        return (
            isPrivate &&
            !auth.isLoading &&
            !auth.isAuthenticated &&
            !hasTriedSignin
        );
    }, [
        isPrivate,
        auth.isLoading,
        auth.isAuthenticated,
        hasTriedSignin,
    ]);

    const getAuthConfig = (path) => {
        if (path?.startsWith("/admin")) {
            return {
                appState: {returnTo: path,},
                authorizationParams: {
                    connection: "admin-db",
                },
            };
        }

        return {
            appState: {returnTo: path,},
            authorizationParams: {
                audience: process.env.REACT_APP_OKTA_AUDIENCE,
                connection: "customers-db",
            },
        };
    };

    useEffect(() => {
        const run = async () => {
            if (auth.isLoading) return;
            if (hasSyncedRef.current) return;
            hasSyncedRef.current = true;

            const user = auth.user;
            const msg = await syncUserWithBackend(user);
            setMessage(msg);
            setIsSynced(true);

            if (shouldRedirect) {
                setHasTriedSignin(true);
                const config = getAuthConfig(locationPath);

                await auth.loginWithRedirect({
                    ...config,
                });
            }
        };
        run();
    }, [auth, shouldRedirect, locationPath]);

    if (!isPrivate) {
        return children;
    }

    if (auth.error) {
        return <h1>Please try again later!</h1>;
    }

    if (auth.isLoading || !isSynced) {
        return <Spinner/>;
    }

    if (auth.isAuthenticated) {
        return React.cloneElement(children, {
            syncMsg: message
        });
    }

    return <Spinner/>;
}

export default AuthorizedComponent;